import type { Bill } from '../data/types';

const WEEKDAY_INDEX: Record<string, number> = {
  sun: 0, sunday: 0, su: 0,
  mon: 1, monday: 1, m: 1,
  tue: 2, tues: 2, tuesday: 2, t: 2,
  wed: 3, weds: 3, wednesday: 3, w: 3,
  thu: 4, thur: 4, thurs: 4, thursday: 4, th: 4,
  fri: 5, friday: 5, f: 5,
  sat: 6, saturday: 6, sa: 6,
};

const MONTH_NAMES: Record<string, number> = {
  jan: 0, january: 0,
  feb: 1, february: 1,
  mar: 2, march: 2,
  apr: 3, april: 3,
  may: 4,
  jun: 5, june: 5,
  jul: 6, july: 6,
  aug: 7, august: 7,
  sep: 8, sept: 8, september: 8,
  oct: 9, october: 9,
  nov: 10, november: 10,
  dec: 11, december: 11,
};

// Parse "MM-DD", "M-D", or "Mon D" / "Month DD" into {month, day} (month 0-indexed).
// Returns null when the cell is unrecognizable so the caller can skip the bill.
function parseMonthDay(raw: string): { month: number; day: number } | null {
  const numeric = /^(\d{1,2})-(\d{1,2})$/.exec(raw);
  if (numeric) {
    const month = parseInt(numeric[1], 10) - 1;
    const day = parseInt(numeric[2], 10);
    if (month < 0 || month > 11 || day < 1 || day > 31) return null;
    return { month, day };
  }
  const named = /^([A-Za-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?$/.exec(raw);
  if (named) {
    const month = MONTH_NAMES[named[1].toLowerCase()];
    const day = parseInt(named[2], 10);
    if (month === undefined || day < 1 || day > 31) return null;
    return { month, day };
  }
  return null;
}

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Day is clamped to the target month's actual length so end-of-month dates
// don't roll forward (e.g. addMonths(May 31, -3) returns Feb 28, not Mar 3).
// JavaScript's Date constructor would happily overflow May 31 → Feb 31 →
// Mar 3, which corrupts back-walks in the bill projection logic.
export function addMonths(d: Date, months: number): Date {
  const year = d.getFullYear();
  const month = d.getMonth() + months;
  const day = d.getDate();
  const last = lastDayOfMonth(year + Math.floor(month / 12), ((month % 12) + 12) % 12);
  return new Date(year, month, Math.min(day, last));
}

export function addDays(d: Date, days: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
}

function lastDayOfMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

// Shift a date to the closest weekday: Saturday → Friday (one day back),
// Sunday → Monday (one day forward). Already-weekday dates pass through.
// Used by the semi-monthly payroll variant where 15th paychecks slide to
// the nearest weekday when the 15th lands on a weekend.
function nearestWeekday(d: Date): Date {
  const dow = d.getDay();
  if (dow === 0) return addDays(d, 1);
  if (dow === 6) return addDays(d, -1);
  return d;
}

// Last Mon–Fri of the given month — walks backward from the last calendar
// day until it hits a weekday. Used by the semi-monthly payroll variant for
// the "last weekday of the month" anchor.
function lastWeekdayOfMonth(year: number, month: number): Date {
  let d = new Date(year, month, lastDayOfMonth(year, month));
  while (d.getDay() === 0 || d.getDay() === 6) {
    d = addDays(d, -1);
  }
  return d;
}

// Anchor-date parser used by biweekly bills. The Sheets proxy returns
// whatever string Google chose to display, which depends on the cell's
// number format — could be ISO ("2026-05-22"), US ("5/22/2026"), or a
// human-friendly "May 22" with no year (Google's default date display).
// Accepting all common formats keeps the spreadsheet authoring forgiving:
// the user picks any one real payday and types it however Sheets formats
// it, and the cycle math walks from there. When the year is missing we
// fall back to the current year — paydays are recent by nature, so this
// rarely needs disambiguation.
function parseAnchorDate(raw: string): Date | null {
  const v = raw.trim();
  let m: RegExpExecArray | null;

  m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(v);
  if (m) return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));

  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(v);
  if (m) return new Date(parseInt(m[3], 10), parseInt(m[1], 10) - 1, parseInt(m[2], 10));

  m = /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/.exec(v);
  if (m) {
    const yy = parseInt(m[3], 10);
    const year = yy < 50 ? 2000 + yy : 1900 + yy;
    return new Date(year, parseInt(m[1], 10) - 1, parseInt(m[2], 10));
  }

  m = /^([A-Za-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})$/.exec(v);
  if (m) {
    const month = MONTH_NAMES[m[1].toLowerCase()];
    if (month !== undefined) {
      return new Date(parseInt(m[3], 10), month, parseInt(m[2], 10));
    }
  }

  m = /^([A-Za-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?$/.exec(v);
  if (m) {
    const month = MONTH_NAMES[m[1].toLowerCase()];
    if (month !== undefined) {
      const year = new Date().getFullYear();
      return new Date(year, month, parseInt(m[2], 10));
    }
  }

  return null;
}

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function dateKey(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export interface Occurrence {
  date: Date;
  bill: Bill;
}

// Stable identifier for an occurrence — lets multiple views agree on which row
// a click should highlight. Two bills sharing a name on the same day would
// collide here, but the sheet's bill names are unique within the workbook.
export function occKey(occ: Occurrence): string {
  return `${dateKey(occ.date)}|${occ.bill.name}`;
}

// Project all due dates for a single bill that fall in [start, end] (inclusive).
// Silently skips bills whose dayOrDate cell can't be parsed for the declared
// frequency — better to omit than to render a bogus date.
export function occurrences(bill: Bill, start: Date, end: Date): Date[] {
  const out: Date[] = [];
  const raw = bill.dayOrDate.trim();
  if (!raw) return out;

  if (bill.frequency === 'one-time') {
    const m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(raw);
    if (!m) return out;
    const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    if (d >= start && d <= end) out.push(d);
    return out;
  }

  if (bill.frequency === 'monthly' || bill.frequency === 'semi-monthly') {
    // Semi-monthly payroll variant: "15, last" or "15 + last" / "15+eom"
    // (case-insensitive). Fires on day N plus the last day of the month,
    // both shifted to the nearest weekday (Sat → Fri, Sun → Mon) so a
    // paycheck never lands on a weekend. Detected first so the numeric
    // path below still owns plain numeric semi-monthly bills.
    if (bill.frequency === 'semi-monthly') {
      const payroll = /^(\d{1,2})\s*[,+]\s*(?:last|eom)$/i.exec(raw);
      if (payroll) {
        const anchorDay = parseInt(payroll[1], 10);
        if (anchorDay < 1 || anchorDay > 31) return out;
        let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
        while (cursor <= end) {
          const year = cursor.getFullYear();
          const month = cursor.getMonth();
          const last = lastDayOfMonth(year, month);
          const anchorDate = nearestWeekday(
            new Date(year, month, Math.min(anchorDay, last))
          );
          if (anchorDate >= start && anchorDate <= end) out.push(anchorDate);
          const lastWeekday = lastWeekdayOfMonth(year, month);
          if (lastWeekday >= start && lastWeekday <= end) out.push(lastWeekday);
          cursor = new Date(year, month + 1, 1);
        }
        return out;
      }
    }

    const day = parseInt(raw, 10);
    if (Number.isNaN(day) || day < 1 || day > 31) return out;
    // Semi-monthly fires twice per month — anchor day and anchor + 15. Both
    // get clamped to the month's actual last day, so day=20 → 20 + (35→last)
    // is fine in February.
    const offsets = bill.frequency === 'semi-monthly' ? [0, 15] : [0];
    let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cursor <= end) {
      const last = lastDayOfMonth(cursor.getFullYear(), cursor.getMonth());
      for (const off of offsets) {
        const d = new Date(
          cursor.getFullYear(),
          cursor.getMonth(),
          Math.min(day + off, last)
        );
        if (d >= start && d <= end) out.push(d);
      }
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }
    return out;
  }

  if (
    bill.frequency === 'yearly' ||
    bill.frequency === 'semi-annually' ||
    bill.frequency === 'quarterly'
  ) {
    const md = parseMonthDay(raw);
    if (!md) return out;
    const stepMonths =
      bill.frequency === 'yearly' ? 12 : bill.frequency === 'semi-annually' ? 6 : 3;

    // Re-derive each occurrence from the original anchor month/day rather
    // than chaining through addMonths. Chaining loses the anchor's day
    // semantics after a single clamp — May 31 → Feb 28 (clamped) →
    // May 28 (clamped again), so the cycle drifts off May 31. By starting
    // from the same `md.day` every step and clamping only to the target
    // month, occurrences stay anchored at the original day-of-month.
    const at = (step: number): Date => {
      const total = md.month + step * stepMonths;
      const year = start.getFullYear() + Math.floor(total / 12);
      const month = ((total % 12) + 12) % 12;
      const last = lastDayOfMonth(year, month);
      return new Date(year, month, Math.min(md.day, last));
    };

    // Walk back until we land at-or-before `start`, then walk forward
    // emitting every occurrence in [start, end].
    let step = 0;
    while (at(step) > start) step--;
    let d = at(step);
    while (d <= end) {
      if (d >= start) out.push(d);
      step++;
      d = at(step);
    }
    return out;
  }

  if (bill.frequency === 'weekly') {
    const target = WEEKDAY_INDEX[raw.toLowerCase()];
    if (target === undefined) return out;
    let cursor = new Date(start);
    while (cursor.getDay() !== target) cursor = addDays(cursor, 1);
    while (cursor <= end) {
      out.push(new Date(cursor));
      cursor = addDays(cursor, 7);
    }
    return out;
  }

  if (bill.frequency === 'biweekly') {
    // Anchor is any one real payday. parseAnchorDate accepts ISO,
    // US-format, and "Month D" / "Mon D" strings (the last assumes the
    // current year). Occurrences walk forward AND backward from the
    // anchor in 14-day steps so calendars before the anchor still render
    // historical paydays.
    const anchor = parseAnchorDate(raw);
    if (!anchor) return out;
    // First on-cycle date at or after `start`. Math.ceil handles the
    // before-anchor case (negative diff) by snapping to the closest
    // earlier-or-equal cycle date that's still ≥ start.
    const diffDays = Math.round(
      (start.getTime() - anchor.getTime()) / 86_400_000,
    );
    const stepsToStart = Math.ceil(diffDays / 14);
    let cursor = addDays(anchor, stepsToStart * 14);
    while (cursor <= end) {
      out.push(new Date(cursor));
      cursor = addDays(cursor, 14);
    }
    return out;
  }

  return out;
}

export function buildOccurrences(bills: Bill[], start: Date, end: Date): Occurrence[] {
  const out: Occurrence[] = [];
  for (const bill of bills) {
    for (const date of occurrences(bill, start, end)) {
      out.push({ date, bill });
    }
  }
  out.sort((a, b) => {
    const t = a.date.getTime() - b.date.getTime();
    return t !== 0 ? t : a.bill.name.localeCompare(b.bill.name);
  });
  return out;
}
